import React, { useState, useEffect } from 'react';
import store, { hmdaActions } from '@apply/adminStore';
import Dialog from '@material-ui/core/Dialog';
import { HmdaApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { StateSelect, PhoneInput, ZipCodeInput } from '../../common/SharedComponents';
import { FederalAgencyOptions, HMDAExportType, HMDAExportTypeOptions } from '../common/HmdaConstants';
import FileDownload from 'js-file-download';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { useSelector } from 'react-redux';

const ExportModal = () => {
    const { isOpen, exportCriteria } = useSelector((state) => state.hmda.hmdaExportModal);
    const { dispatch } = store;
    const hmdaApi = new HmdaApi.HmdaApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const [id, set_id] = useState(0);
    const [recordIdentifier, set_recordIdentifier] = useState(1);
    const [financialInstitutionName, set_financialInstitutionName] = useState('');
    const [calendarYear, set_calendarYear] = useState('');
    const [calendarQuarter, set_calendarQuarter] = useState(4);
    const [contactName, set_contactName] = useState('');
    const [contactPhoneNumber, set_contactPhoneNumber] = useState('');
    const [contactEmail, set_contactEmail] = useState('');
    const [contactStreetAddress, set_contactStreetAddress] = useState('');
    const [contactCity, set_contactCity] = useState('');
    const [contactState, set_contactState] = useState('');
    const [contactZipCode, set_contactZipCode] = useState('');
    const [filingAgencyId, set_filingAgencyId] = useState('');
    const [federalTaxpayerIdentificationNumber, set_federalTaxpayerIdentificationNumber] = useState('');
    const [legalEntityIdentifier, set_LEI] = useState('');

    const [availableYears, set_availableYears] = useState([]);

    const [exportType, set_exportType] = useState(HMDAExportType.TextPipeDelimited);

    const styles = {
        sectionHeader: { margin: '10px 0' },
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px' },
    };

    useEffect(() => {
        async function loadInformation() {
            await loadAvailableFilingYears();
            await loadFilingInfo();

            set_loadingOpen(false);
        }
        if (isOpen) {
            loadInformation();
        }
    }, [isOpen]);

    const loadAvailableFilingYears = async () => {
        const availableYearsResponse = await hmdaApi.getAvailableFilingYears();
        if (availableYearsResponse.statusCode !== 200) return;

        const yearsToChoose = availableYearsResponse.result;
        determineCalendarYear(yearsToChoose);
        set_availableYears(availableYearsResponse.result);
    };

    const loadFilingInfo = async () => {
        const filingInfoResponse = await hmdaApi.getExportFilingInformation();
        if (filingInfoResponse.statusCode !== 200) return;

        setState(filingInfoResponse.result);
    };

    const determineCalendarYear = (yearArray) => {
        if (yearArray !== undefined && yearArray !== null) {
            let mostRecentYear = Math.max(...yearArray.map((a) => a.value));
            set_calendarYear(mostRecentYear);
        } else {
            set_calendarYear('');
        }
    };

    const setState = (entity) => {
        if (entity) {
            set_id(entity.id);
            set_recordIdentifier(entity.recordIdentifier);
            set_financialInstitutionName(entity.financialInstitutionName);
            set_contactName(entity.contactName);
            set_contactPhoneNumber(entity.contactPhoneNumber);
            set_contactEmail(entity.contactEmail);
            set_contactStreetAddress(entity.contactStreetAddress);
            set_contactCity(entity.contactCity);
            set_contactState(entity.contactState);
            set_contactZipCode(entity.contactZipCode);
            set_filingAgencyId(entity.filingAgencyId);
            set_federalTaxpayerIdentificationNumber(entity.federalTaxpayerIdentificationNumber);
            set_LEI(entity.legalEntityIdentifier);
        } else {
            resetState();
        }
    };

    const resetState = () => {
        set_recordIdentifier(1);
        set_financialInstitutionName('');
        set_calendarYear('');
        set_calendarQuarter(4);
        set_contactName('');
        set_contactPhoneNumber('');
        set_contactEmail('');
        set_contactStreetAddress('');
        set_contactCity('');
        set_contactState('');
        set_contactZipCode('');
        set_filingAgencyId('');
        set_federalTaxpayerIdentificationNumber('');
        set_LEI('');
    };

    const handleClose = () =>
        dispatch(
            hmdaActions.setHmdaExportModal({
                hmdaExportModal: { isOpen: false },
            })
        );

    const saveChanges = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                id: id,
                recordIdentifier: recordIdentifier,
                financialInstitutionName: financialInstitutionName,
                calendarYear: calendarYear,
                calendarQuarter: calendarQuarter,
                contactName: contactName,
                contactPhoneNumber: contactPhoneNumber,
                contactEmail: contactEmail,
                contactStreetAddress: contactStreetAddress,
                contactCity: contactCity,
                contactState: contactState,
                contactZipCode: contactZipCode,
                filingAgencyId: filingAgencyId,
                federalTaxpayerIdentificationNumber: federalTaxpayerIdentificationNumber,
                legalEntityIdentifier: legalEntityIdentifier,
            };
            const response = await hmdaApi.setExportFilingInformation(model);

            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            if (response.statusCode === 200) {
                let date = new Date();
                let dateFormat = `${
                    date.getUTCMonth() + 1
                }-${date.getUTCDate()}-${date.getUTCFullYear()}-${date.getUTCHours()}-${date.getUTCMinutes()}-${date.getUTCSeconds()}`;
                let fileName = 'BFIA_HMDA_' + dateFormat;
                if (exportType === HMDAExportType.TextPipeDelimited) {
                    hmdaApi.exportGridAsText(exportCriteria).then((response) => {
                        //Download File:
                        FileDownload(response, fileName + '.txt');
                    });
                }
                if (exportType === HMDAExportType.Excel) {
                    hmdaApi.exportGridAsExcel(exportCriteria).then((response) => {
                        //Download File:
                        FileDownload(response, fileName + '.xlsx');
                    });
                }
                if (exportType === HMDAExportType.ExcelTransposed) {
                    hmdaApi.exportGridAsExcelTransposed(exportCriteria).then((response) => {
                        //Download File:
                        FileDownload(response, fileName + '_Transposed.xlsx');
                    });
                }
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('HMDA Filing Information Saved.');
            set_loadingOpen(false);

            setTimeout(() => {
                handleClose();
            }, 600);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='md'
                open={isOpen}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '1100px', overflow: 'unset' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1>Export HMDA Data</h1>
                            </div>
                        </div>
                        <div
                            className='main-content no-search'
                            style={{ padding: '0', width: '100%' }}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <h2>Only HMDA entries applicable for export are included.</h2>
                            </div>
                            <form
                                name='exportHmdaForm'
                                id='exportHmdaForm'
                                onSubmit={(event) => saveChanges(event)}
                                className={'standard-form'}
                            >
                                <div style={{ padding: '0px 8px 0 8px' }}>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Exportable File Types
                                    </h2>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='exportType'>Export Type</label>
                                            <select
                                                id='exportType'
                                                value={exportType}
                                                onChange={(event) => set_exportType(parseInt(event.target.value))}
                                                required={true}
                                            >
                                                {HMDAExportTypeOptions.map((t) => {
                                                    return (
                                                        <option
                                                            key={t.id}
                                                            value={t.id}
                                                        >
                                                            {t.name}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Required for Federal Submittal, value of 1.</span>
                                            </span>
                                        </div>
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Institution Information
                                    </h2>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 large-2'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='recordIdentifier'>
                                                Record Identifier <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='recordIdentifier'
                                                type='text'
                                                value={recordIdentifier}
                                                onChange={(event) => set_recordIdentifier(event.target.value)}
                                                required={true}
                                                disabled={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Required for Federal Submittal, value of 1.</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 large-6'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='financialInstitutionName'>
                                                Financial Institution Name <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='financialInstitutionName'
                                                type='text'
                                                value={financialInstitutionName}
                                                onChange={(event) => set_financialInstitutionName(event.target.value)}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: Ficus Bank</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 large-2'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='calendarYear'>
                                                Calendar Year <span className='req'>*</span>
                                            </label>
                                            <select
                                                id='calendarYear'
                                                value={calendarYear}
                                                onChange={(event) => set_calendarYear(event.target.value)}
                                                required={true}
                                            >
                                                <option
                                                    key=''
                                                    value=''
                                                ></option>
                                                {availableYears.map((t) => {
                                                    return (
                                                        <option
                                                            key={t.value}
                                                            value={t.value}
                                                        >
                                                            {t.display}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: 2022</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 large-2'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='calendarQuarter'>
                                                Filing Quarter Type
                                                <span className='req'>*</span>
                                            </label>
                                            <select
                                                id={'calendarQuarter'}
                                                value={calendarQuarter}
                                                onChange={(event) => set_calendarQuarter(event.target.value)}
                                                required={true}
                                                disabled={true}
                                            >
                                                <option value='1'>1. Quarter 1</option>
                                                <option value='2'>2. Quarter 2</option>
                                                <option value='3'>3. Quarter 3</option>
                                                <option value='4'>4. Annual</option>
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>
                                                    Description: 4. Annual submissions for all HMDA filers for covered loans and
                                                    applications with respect to which final action was taken from January 1st
                                                    (01/01) and December 31st (12/31). Codes 1,2, and 3 are used only during
                                                    quarterly filing as required.
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Contact Information
                                    </h2>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 medium-6 large-6'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='contactName'>
                                                Name <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='contactName'
                                                type='text'
                                                value={contactName}
                                                onChange={(event) => set_contactName(event.target.value)}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: Erika Otis</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 medium-3 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='contactPhoneNumber'>
                                                Telephone Number <span className='req'>*</span>
                                            </label>
                                            <PhoneInput
                                                id='contactPhoneNumber'
                                                value={contactPhoneNumber}
                                                onChange={(value) => set_contactPhoneNumber(value)}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: 999-999-9999</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 medium-3 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='contactEmail'>
                                                Email
                                                <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='contactEmail'
                                                type='email'
                                                value={contactEmail}
                                                onChange={(event) => set_contactEmail(event.target.value)}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: erikaotis@bank.com</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='contactStreetAddress'>
                                                Street Address
                                                <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='contactStreetAddress'
                                                type='text'
                                                value={contactStreetAddress}
                                                onChange={(event) => set_contactStreetAddress(event.target.value)}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: 4321 W Random Blvd Ste 201</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 medium-3 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='contactCity'>
                                                City
                                                <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='contactCity'
                                                type='text'
                                                value={contactCity}
                                                onChange={(event) => set_contactCity(event.target.value)}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: Somecity</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 medium-3 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='contactState'>
                                                State
                                                <span className='req'>*</span>
                                            </label>
                                            <StateSelect
                                                id='contactState'
                                                value={contactState}
                                                onChange={(event) => set_contactState(event.target.value)}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: CA</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 medium-3 large-2'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='contactZipCode'>
                                                Zip
                                                <span className='req'>*</span>
                                            </label>
                                            <ZipCodeInput
                                                id='contactZipCode'
                                                value={contactZipCode}
                                                onChange={(event) => set_contactZipCode(event.target.value)}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: 90049-9998</span>
                                            </span>
                                        </div>
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Federal Filing Information
                                    </h2>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 medium-4 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='filingAgencyId'>
                                                Federal Agency
                                                <span className='req'>*</span>
                                            </label>
                                            <select
                                                id='filingAgencyId'
                                                value={filingAgencyId}
                                                onChange={(event) => set_filingAgencyId(event.target.value)}
                                                required={true}
                                            >
                                                <option>Select</option>
                                                {FederalAgencyOptions.map((agency) => {
                                                    return (
                                                        <option
                                                            key={agency.id}
                                                            value={agency.id}
                                                        >
                                                            {agency.name}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <div
                                            className='columns small-12 medium-4 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='federalTaxpayerIdentificationNumber'>
                                                Federal Taxpayer Identification Number
                                                <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='federalTaxpayerIdentificationNumber'
                                                type='text'
                                                value={federalTaxpayerIdentificationNumber}
                                                onChange={(event) => set_federalTaxpayerIdentificationNumber(event.target.value)}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Example: 99-9999999</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 medium-4 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='legalEntityIdentifier'>
                                                Legal Entity Identifier (LEI)
                                                <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='legalEntityIdentifier'
                                                type='text'
                                                value={legalEntityIdentifier}
                                                onChange={(event) => set_LEI(event.target.value)}
                                                maxLength='20'
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>
                                                    20 Character Legal Identifier - Example: 10BX939C5543TQA1144M
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                            title='Export HMDA Filing'
                                        >
                                            Export
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={handleClose}
                                            title='Abandon changes and close this window'
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default ExportModal;
